import { cva, type VariantProps } from 'class-variance-authority';
import { disabledState, focusRingOffset, uiMarker } from './shared';

/**
 * THE DISPATCH MAP
 *
 * What sits on top of the map tiles: a driver's pin, a labelled point (a
 * pickup, a drop, a call waiting for a driver), and the timeline and replay
 * bar beside them.
 *
 * The pins take their colour from the JOB PHASE tokens, the same ones a
 * phase Badge uses, so a driver who reads "towing" in a list is the same red
 * on the map. The old map painted every driver one blue and every job one
 * red, from hex constants, because Google's own markers cannot read a CSS
 * variable; these are HTML laid over the map, so they can.
 */

/** The button a pin is, so it has a tab stop and a 44px target. */
export const mapPinRecipe = cva(
  [
    uiMarker,
    'inline-flex h-11 items-center rounded-pill bg-transparent p-0',
    focusRingOffset,
    disabledState,
  ],
  {
    variants: {
      interactive: { true: 'cursor-pointer', false: 'cursor-default' },
    },
    defaultVariants: { interactive: true },
  }
);

/**
 * The tones a driver's pin comes in: a job phase while they are on a call,
 * and three states for when they are not. `available` and `offline` are
 * outlined rather than filled, so "free" and "no signal" are told apart from
 * a working truck by shape as well as hue.
 *
 * Each tone names its own border colour. A shared one in the base list would
 * sit beside the tone's in the class string, and which of two `border-*`
 * colours wins is then stylesheet order, not this file.
 */
const pinTones = {
  'phase-dispatched':
    'border-background bg-phase-dispatched text-phase-dispatched-foreground',
  'phase-en-route':
    'border-background bg-phase-en-route text-phase-en-route-foreground',
  'phase-on-scene':
    'border-background bg-phase-on-scene text-phase-on-scene-foreground',
  'phase-towing':
    'border-background bg-phase-towing text-phase-towing-foreground',
  'phase-returning':
    'border-background bg-phase-returning text-phase-returning-foreground',
  available: 'border-success bg-background text-success',
  away: 'border-border bg-muted text-muted-foreground',
  offline:
    'border-dashed border-muted-foreground bg-background text-muted-foreground',
} as const;

/** The round badge with the driver's initials. */
export const driverPinDotRecipe = cva(
  [
    uiMarker,
    'relative z-[1] flex size-9 shrink-0 items-center justify-center rounded-pill border-2 font-sans text-caption-strong shadow-e2',
  ],
  {
    variants: {
      tone: pinTones,
      /** The pin whose call or driver is open in the panel. */
      selected: { true: 'ring-4 ring-ring/40', false: '' },
    },
    defaultVariants: { tone: 'available', selected: false },
  }
);

/** The words tucked behind the dot: an ETA, "On scene", how long since a fix. */
export const driverPinTagRecipe = cva([
  uiMarker,
  '-ml-3 whitespace-nowrap rounded-r-pill bg-background py-1 pl-5 pr-3 font-sans text-caption-strong tabular-nums text-foreground shadow-e1',
]);

/**
 * A labelled point: where a call is picked up or dropped, a call still
 * waiting for a driver, or a finished call on the history map.
 */
export const mapLabelPinRecipe = cva(
  [
    uiMarker,
    'whitespace-nowrap rounded-pill border-2 px-3 py-1 font-sans text-caption-strong shadow-e1',
  ],
  {
    variants: {
      tone: {
        pickup: 'border-foreground bg-background text-foreground',
        // Filled where the pickup is outlined, so the two ends of a call are
        // told apart by shape. It was the towing red once, and sat beside a
        // waiting call's red looking like the same thing.
        drop: 'border-foreground bg-foreground text-background',
        waiting: 'border-destructive bg-background text-destructive',
        past: 'border-border bg-background text-muted-foreground',
      },
    },
    defaultVariants: { tone: 'pickup' },
  }
);

/**
 * A finished call on the history map, as a small dot: many of them sit on
 * one map, and a label on each buried the call being looked at. The number
 * shows beside the dot on hover or focus only.
 */
export const mapDotPinRecipe = cva([
  uiMarker,
  'group relative inline-flex size-11 cursor-pointer items-center justify-center rounded-pill bg-transparent p-0',
  focusRingOffset,
]);

export const mapDotPinDotRecipe = cva([
  uiMarker,
  'size-3 rounded-pill border-2 border-background bg-muted-foreground shadow-e1',
]);

export const mapDotPinLabelRecipe = cva([
  uiMarker,
  'pointer-events-none absolute left-full top-1/2 hidden -translate-x-2 -translate-y-1/2 whitespace-nowrap rounded-pill border-2 border-border bg-background px-3 py-1 font-sans text-caption-strong text-muted-foreground shadow-e1',
  'group-hover:block group-focus-visible:block',
]);

/**
 * Several calls whose labels would sit on top of each other at this zoom,
 * drawn as one dark circle with the count in it. Clicking it closes in on
 * them. Two sizes: a bigger one from ten calls up, so a crowd reads as one.
 * The lighter ring is what tells it apart from a driver's dot.
 */
export const mapClusterRecipe = cva(
  [
    uiMarker,
    'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-pill border-2 border-background bg-foreground font-sans tabular-nums text-background shadow-e2 ring-4 ring-foreground/25',
    focusRingOffset,
  ],
  {
    variants: {
      size: {
        sm: 'size-9 text-caption-strong',
        lg: 'size-11 text-body-sm-strong',
      },
    },
    defaultVariants: { size: 'sm' },
  }
);

/**
 * The still sketch of a map shown while a route loads: the muted ground the
 * streets are drawn on, and the pill in the middle that says what is loading.
 */
export const mapSkeletonRecipe = cva([
  uiMarker,
  'relative flex items-center justify-center overflow-hidden bg-muted',
]);

export const mapSkeletonLabelRecipe = cva([
  uiMarker,
  'relative rounded-pill border border-border bg-background px-4 py-2 shadow-e1',
]);

/** The mark in front of one step of a call's timeline. */
export const timelineDotRecipe = cva(
  [uiMarker, 'size-3 shrink-0 rounded-pill border-2'],
  {
    variants: {
      state: {
        done: 'border-foreground bg-foreground',
        current: 'border-primary bg-primary',
        upcoming: 'border-muted-foreground bg-background',
      },
    },
    defaultVariants: { state: 'upcoming' },
  }
);

/** One step of the timeline when it can be clicked to move the replay. */
export const timelineStepRecipe = cva(
  [uiMarker, 'flex w-full items-center gap-3 bg-transparent py-1.5 text-left'],
  {
    variants: {
      interactive: {
        true: [
          'cursor-pointer rounded-md hover:bg-accent',
          focusRingOffset,
        ].join(' '),
        false: '',
      },
    },
    defaultVariants: { interactive: false },
  }
);

/** The strip under the replay slider, one piece per leg of the call. */
export const replayLegsRecipe = cva([uiMarker, 'flex h-2 w-full gap-0.5']);

export const replayLegRecipe = cva(
  [uiMarker, 'h-full min-w-1 first:rounded-l-pill last:rounded-r-pill'],
  {
    variants: {
      phase: {
        'en-route': 'bg-phase-en-route',
        'on-scene': 'bg-phase-on-scene',
        towing: 'bg-phase-towing',
      },
    },
    defaultVariants: { phase: 'en-route' },
  }
);

export type DriverPinDotRecipeProps = VariantProps<typeof driverPinDotRecipe>;
export type MapLabelPinRecipeProps = VariantProps<typeof mapLabelPinRecipe>;
export type MapClusterRecipeProps = VariantProps<typeof mapClusterRecipe>;
export type TimelineDotRecipeProps = VariantProps<typeof timelineDotRecipe>;
export type ReplayLegRecipeProps = VariantProps<typeof replayLegRecipe>;
